import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {useTheme,IconButton,Stack,Badge,Menu,Typography,Tooltip,List,ListItem,ListItemText,Box,Divider,Button} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DeleteIcon from '@mui/icons-material/Delete';
import {formatAppDateTime} from "../../utilities/Utilities.js";
import {useNotifications} from './NotificationsContext.js';
import "dayjs/locale/en";
import "dayjs/locale/fi";
import "dayjs/locale/pt-br";

function NotificationsPopup({application}){
    const [anchorEl,setAnchorEl]=useState(null);
    const lang=application?.globalData?.lang;
    const locale=(application?.globalData?.locale||lang||"en").toLowerCase();
    const navigate=useNavigate();
    const theme=useTheme();
    const {notifications,unread,total,loaded,deleteNotification,clickOnNotification,getNotificationIcon,getNotificationColor,updateNotification}=useNotifications();

    const renderSimpleMessage=(n)=>{
        const parts=n.message.split(/(\{[^}]+\})/g);
        return parts.map((part,index)=>{
            if(part.startsWith("{")&&part.endsWith("}")&&n.link){
                const label=part.slice(1,-1);
                const style={
                    color:theme.list_link_color,
                    fontWeight:'normal',
                    fontSize:"0.7rem",
                    cursor:"pointer"
                };
        
                return (
                    <Typography
                        key={index}
                        component="span"
                        style={style}
                        sx={{"&:hover":{textDecoration:"underline"}}}
                        onClick={async(e)=>{
                            e.stopPropagation();
                            e.preventDefault();
                            if(!n.link) return;
                            if(!n.read&&!(await updateNotification(n.id))) return;
                            handleClose();
                            navigate(n.link);
                        }}>
                        {label}
                    </Typography>
                );
            }
            return part;
        });
    };

    const handleOpen=(event)=>{
        setAnchorEl(event.currentTarget);
    };

    const handleClose=()=>{
        setAnchorEl(null);
    };

    return (
    <>
      <IconButton color="inherit" onClick={handleOpen}>
        <Badge 
            badgeContent={unread} 
            color="error" 
            invisible={unread===0}
            sx={{"& .MuiBadge-badge":{fontSize:"0.6rem",height:16,minWidth:16,padding:"0 4px",top:6,right:4}}}>
          <NotificationsIcon/>
        </Badge>
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <Stack
            direction={{xs:"column",sm:"row"}}
            alignItems={{xs:"flex-start",sm:"center"}}
            justifyContent="space-between"
            spacing={{xs:0.5,sm:1}}
            sx={{px:2,py:0.5}}>
            <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{fontSize:"0.9rem"}}>
                {notifications.length===0?application.getLocale("NotificationsNone"):application.getLocale("TooltipNotifications")}
            </Typography>
            <Typography
                variant="caption"
                sx={{
                    color:"text.secondary",
                    alignSelf:{xs:"flex-start",sm:"center"}}}>
                        {(application.getLocale("NotificationsTotalVsUnread")||"{UNREAD} unread • {TOTAL} total").replace("{UNREAD}", unread).replace("{TOTAL}", total)}
            </Typography>
            <Button
                size="small"
                onClick={(e)=>{
                    e.stopPropagation();
                    handleClose();
                    navigate('/my_notifications');
                }}
                sx={{fontSize:"0.65rem",minWidth:"auto",px:1,alignSelf:{xs:"flex-end",sm:"center"}}}>
                {application.getLocale("NotificationsViewAll")}
            </Button>
        </Stack>
        {total>loaded&&(
            <Typography
                variant="caption"
                sx={{display:"block",color:"text.secondary",fontSize:"0.65rem",py:0.5,px:2,textAlign:"left"}}>
                {(application.getLocale("NotificationsShowMaxLoaded")||"Showing latest {LOADED} of {TOTAL} notifications").replace("{LOADED}", loaded).replace("{TOTAL}", total)}
            </Typography>
        )}
        <Divider />
        <List sx={{maxHeight:320,overflowY:"auto",p:0}}>
            {notifications.map((n,index)=>(
                <ListItem
                    key={n.id}
                    sx={{alignItems:"flex-start",bgcolor:n.read?"background.paper":"action.hover",py:0.5,"&:hover":{bgcolor:n.read?"action.hover":"action.selected"},borderBottom:index!==notifications.length-1?"1px solid rgba(0,0,0,0.06)":"none"}}
                    secondaryAction={
                        <Tooltip title={application.getLocale("NotificationsDelete")}>
                            <IconButton 
                                size="small" 
                                sx={{p:0.5}}
                                onClick={(e)=>{
                                    e.stopPropagation();
                                    deleteNotification([n.id]);
                                }}>
                                <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                        </Tooltip>
                    }>
                    <ListItemText
                        sx={{borderLeft:"3px solid",borderColor:getNotificationColor(n.type_db),pl:1}}
                        primary={
                            <Box sx={{display:"flex",alignItems:"center",gap:1}}>
                                <Tooltip title={n.type}>
                                    <Box sx={{display:"flex"}}>
                                        {getNotificationIcon(n.type_db)}
                                    </Box>
                                </Tooltip>
                                <Box sx={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                    <Typography 
                                        variant="body2" 
                                        fontWeight="bold" 
                                        noWrap 
                                        sx={{cursor:"pointer",transition:"all 0.2s ease","&:hover":{color:"primary.main",textDecoration:"underline"}}}
                                        onClick={(e)=>{
                                            e.stopPropagation();
                                            handleClose();
                                            clickOnNotification(n);
                                        }}>
                                        {n.title}
                                    </Typography>
                                    {n.read&&n.read_at&&(
                                        <Typography variant="caption" sx={{ml:1,color:"text.secondary",fontSize:"0.65rem"}}>
                                            {(application.getLocale("NotificationsReadOn")||"(Read on {READ_AT})").replace("{READ_AT}", formatAppDateTime(n.read_at,locale))}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        }
                        secondary={
                            <Typography variant="caption" sx={{display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden",fontSize:"0.7rem"}}>
                                {renderSimpleMessage(n)}
                            </Typography>
                        }
                    />
                </ListItem>
            ))}
        </List>
      </Menu>
    </>
  );
}

export default NotificationsPopup;